/**
 * Map → backyard travel load profiler.
 * Marks phases with performance.now() and logs a summary when travel completes.
 * Enable with ?perf=1 or localStorage porchlight-perf=1; always records when DEV.
 */

export type TravelPhase =
  | 'map_pin_click'
  | 'pin_bounce_done'
  | 'route_transition_start'
  | 'fade_out_done'
  | 'scene_creation_start'
  | 'background_request_start'
  | 'background_downloaded'
  | 'background_decoded'
  | 'prop_assets_requested'
  | 'prop_assets_downloaded'
  | 'scene_objects_created'
  | 'first_backyard_frame'
  | 'movement_enabled';

export interface TravelPhaseMark {
  phase: TravelPhase;
  t: number;
  detail?: string;
}

export interface TravelLoadReport {
  destination: string;
  marks: TravelPhaseMark[];
  durationsMs: Record<string, number>;
  totalMs: number;
  resourceSummary?: TravelResourceSummary;
}

export interface TravelResourceSummary {
  requestCount: number;
  totalBytes: number;
  largest: Array<{ url: string; bytes: number; durationMs: number; cached: boolean }>;
  cacheHits: number;
}

const MARK_PREFIX = 'porchlight-travel:';
const STORAGE_KEY = 'porchlight-perf';

let active: {
  destination: string;
  t0: number;
  marks: TravelPhaseMark[];
} | null = null;

let lastReport: TravelLoadReport | null = null;

export function isTravelPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.DEV) return true;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('perf') === '1') return true;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function beginTravelLoad(destination: string): void {
  active = {
    destination,
    t0: performance.now(),
    marks: [],
  };
  markTravelPhase('map_pin_click', destination);
}

export function markTravelPhase(phase: TravelPhase, detail?: string): void {
  if (!active) return;
  const t = performance.now();
  active.marks.push({ phase, t, detail });
  try {
    performance.mark(`${MARK_PREFIX}${phase}`, {
      detail: { destination: active.destination, detail, elapsedMs: t - active.t0 },
    });
  } catch {
    try {
      performance.mark(`${MARK_PREFIX}${phase}`);
    } catch {
      /* ignore */
    }
  }
  if (isTravelPerfEnabled()) {
    // eslint-disable-next-line no-console
    console.info(
      `[TravelPerf] +${(t - active.t0).toFixed(0)}ms  ${phase}${detail ? ` — ${detail}` : ''}`,
    );
  }
}

function collectResourceSummary(sinceMs: number): TravelResourceSummary | undefined {
  if (typeof performance.getEntriesByType !== 'function') return undefined;
  const navStart = performance.timeOrigin;
  const absoluteSince = navStart + sinceMs;
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const relevant = entries.filter((e) => {
    const abs = navStart + e.startTime;
    return abs >= absoluteSince - 50;
  });
  // Prefer art assets from this travel window (masters + props).
  const art = relevant.filter((e) => /\/art\//.test(e.name) || /Master%20|Backyard|\.png|\.webp|\.avif/i.test(e.name));
  const list = art.length > 0 ? art : relevant;
  const largest = [...list]
    .map((e) => {
      const bytes = e.transferSize || e.encodedBodySize || e.decodedBodySize || 0;
      const cached = e.transferSize === 0 && e.decodedBodySize > 0;
      return {
        url: e.name,
        bytes,
        durationMs: e.duration,
        cached,
      };
    })
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 15);
  const totalBytes = list.reduce(
    (sum, e) => sum + (e.transferSize || e.encodedBodySize || 0),
    0,
  );
  const cacheHits = list.filter(
    (e) => e.transferSize === 0 && e.decodedBodySize > 0,
  ).length;
  return {
    requestCount: list.length,
    totalBytes,
    largest,
    cacheHits,
  };
}

export function finishTravelLoad(): TravelLoadReport | null {
  if (!active) return null;
  const marks = active.marks;
  const t0 = active.t0;
  const destination = active.destination;
  const durationsMs: Record<string, number> = {};
  for (let i = 1; i < marks.length; i += 1) {
    const prev = marks[i - 1]!;
    const cur = marks[i]!;
    durationsMs[`${prev.phase}→${cur.phase}`] = cur.t - prev.t;
  }
  const totalMs = marks.length > 0 ? marks[marks.length - 1]!.t - t0 : 0;
  const resourceSummary = collectResourceSummary(t0);
  const report: TravelLoadReport = {
    destination,
    marks: marks.map((m) => ({ ...m, t: m.t - t0 })),
    durationsMs,
    totalMs,
    resourceSummary,
  };
  lastReport = report;
  active = null;

  if (isTravelPerfEnabled()) {
    // eslint-disable-next-line no-console
    console.info('[TravelPerf] SUMMARY', {
      destination,
      totalMs: Math.round(totalMs),
      phases: report.marks.map((m) => `${m.phase}@${m.t.toFixed(0)}`),
      durationsMs: Object.fromEntries(
        Object.entries(durationsMs).map(([k, v]) => [k, Math.round(v)]),
      ),
      resources: resourceSummary
        ? {
            requests: resourceSummary.requestCount,
            bytes: resourceSummary.totalBytes,
            cacheHits: resourceSummary.cacheHits,
            largest: resourceSummary.largest.slice(0, 8).map((r) => ({
              kb: Math.round(r.bytes / 1024),
              ms: Math.round(r.durationMs),
              cached: r.cached,
              file: decodeURIComponent(r.url.split('/').pop() ?? r.url),
            })),
          }
        : undefined,
    });
  }

  try {
    (window as unknown as { __porchlightTravelPerf?: TravelLoadReport }).__porchlightTravelPerf =
      report;
  } catch {
    /* ignore */
  }
  return report;
}

export function getLastTravelLoadReport(): TravelLoadReport | null {
  return lastReport;
}

/** Wait until an <img> has downloaded + decoded (or already complete). */
export async function waitForImageReady(
  img: HTMLImageElement,
  src?: string,
): Promise<{ downloadedMs: number; decodedMs: number }> {
  const t0 = performance.now();
  if (src && img.getAttribute('src') !== src) {
    img.src = src;
  }
  if (!img.getAttribute('src') && !img.src) {
    return { downloadedMs: 0, decodedMs: 0 };
  }

  if (!img.complete || img.naturalWidth === 0) {
    await new Promise<void>((resolve, reject) => {
      const onLoad = (): void => {
        cleanup();
        resolve();
      };
      const onError = (): void => {
        cleanup();
        reject(new Error(`Image failed: ${img.currentSrc || img.src}`));
      };
      const cleanup = (): void => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
      };
      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
    });
  }
  const downloadedMs = performance.now() - t0;
  const tDecode = performance.now();
  try {
    if (typeof img.decode === 'function') {
      await img.decode();
    }
  } catch {
    /* decode can reject if removed; treat as ready if complete */
  }
  return {
    downloadedMs,
    decodedMs: performance.now() - tDecode,
  };
}

/** Preload URLs into the browser HTTP cache (and decode) without attaching to DOM. */
const preloadCache = new Map<string, Promise<void>>();

export function preloadImageUrl(url: string): Promise<void> {
  const existing = preloadCache.get(url);
  if (existing) return existing;
  const promise = (async () => {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    try {
      await waitForImageReady(img);
    } catch {
      /* keep cache entry so we don't spin forever; allow retry by deleting */
      preloadCache.delete(url);
    }
  })();
  preloadCache.set(url, promise);
  return promise;
}

export function preloadImageUrls(urls: string[]): Promise<void[]> {
  return Promise.all(urls.filter(Boolean).map((u) => preloadImageUrl(u)));
}

export function isImageUrlPreloaded(url: string): boolean {
  return preloadCache.has(url);
}
